/**
 * `manage-party-alias` — FR-29, NFR-13, NFR-07. D-B8 (CLM-56); closes B-13.
 *
 * One endpoint, a discriminated union of four actions, the `manage-clause`
 * shape: the body *is* the action, there is no envelope, and every member is
 * `.strict()` so a typo in a field name fails validation instead of being
 * silently dropped on a compliance write.
 *
 * ## Why an eighth endpoint and not a ninth `manage-clause` action
 *
 * The party-alias list **is** the FR-29 control: substitution is string
 * replacement of known values (§4A.1), so what a human approves here is exactly
 * what leaves the tenant tokenised. Clause-*search* aliases are a different table
 * (`clm.clause_search_alias`) with different semantics — search tuning, WP-09 §9.
 * Filing a compliance control under search tuning would put Legal's FR-29
 * judgement behind `search:tune`; this endpoint has its own action,
 * `party:manage_alias`, held by admin and Legal.
 *
 * ## The four actions and the row states they move between
 *
 * There is no status column (migration 0004). A state is derived from `source`,
 * `is_confirmed` and `is_active` — see `PartyAliasStateSchema`:
 *
 *  - `propose`  — a human adds a `Manual` alias. Confirmed **at insert by its
 *                 proposer** (the single-approver model, G1): `ck_party_alias_
 *                 short_confirmed` makes a short alias unrepresentable otherwise,
 *                 and the proposer already holds the approving action. → `active`.
 *  - `approve`  — a `pending` leak-detector proposal becomes `active`.
 *  - `reject`   — a `pending` proposal is turned down. The row is kept
 *                 (`is_active = false`) so `uq_party_alias` blocks the same
 *                 variant from being re-queued by the next run. → `rejected`.
 *  - `retire`   — an `active` alias stops being substituted. → `retired`. The
 *                 `Qfly` canonical name cannot be retired (422): it is confirmed
 *                 by being canonical, and retiring it would un-substitute the
 *                 client's legal name.
 *
 * Each call is one INSERT or one UPDATE inside one attributed transaction, and
 * the `clm.audit_log` row is written by `tg_party_alias_audit` (migration 0046)
 * — the handler writes no second row (NFR-13 is *one* row per write). State
 * preconditions are enforced in the statement's `WHERE`, so a race is a `409`
 * from a 0-row UPDATE, never a read-then-write.
 *
 * ## Addressing
 *
 * By the natural key `(party_key, alias_text)`, case-insensitive
 * (`uq_party_alias_ci`) — the `acknowledge-notification` precedent. No surrogate
 * id crosses the wire.
 */
import { z } from 'zod';
import { PartyKeySchema } from '../primitives.js';
import { AuditReceiptSchema } from '../provenance.js';
import { PartyAliasSchema, PartyAliasStateSchema } from '../domain/party.js';
export const MANAGE_PARTY_ALIAS_ACTIONS = ['propose', 'approve', 'reject', 'retire'];
/** The natural key. `alias_text` matches case-insensitively and trimmed, as `uq_party_alias_ci` does. */
const aliasAddress = {
    party_key: PartyKeySchema,
    alias_text: z.string().min(1).max(200),
};
/**
 * `note` (propose/approve) and `reason` (reject/retire) both land in
 * `party_alias.decision_note` (migration 0084). The distinct names make *"a
 * rejection without a reason"* a `400` at the schema, not a convention.
 */
export const ManagePartyAliasRequestSchema = z.discriminatedUnion('action', [
    z
        .object({
        action: z.literal('propose'),
        ...aliasAddress,
        note: z.string().min(1).max(1000).optional(),
    })
        .strict(),
    z
        .object({
        action: z.literal('approve'),
        ...aliasAddress,
        note: z.string().min(1).max(1000).optional(),
    })
        .strict(),
    z
        .object({
        action: z.literal('reject'),
        ...aliasAddress,
        reason: z.string().min(1).max(1000),
    })
        .strict(),
    z
        .object({
        action: z.literal('retire'),
        ...aliasAddress,
        reason: z.string().min(1).max(1000),
    })
        .strict(),
]);
export const ManagePartyAliasResponseSchema = z.object({
    party_key: PartyKeySchema,
    /** The row after the write. */
    alias: PartyAliasSchema,
    /** `null` for `propose` — there was no row before. */
    previous_state: PartyAliasStateSchema.nullable(),
    /**
     * FR-29 consequence, stated on the wire: `true` when the **active** alias set
     * changed (propose, approve, retire), `false` for reject. Nothing is bumped by
     * hand — `pseudonym_map_version` is a content hash recomputed at every compile
     * (`packages/pseudonymisation/src/map.ts`), so the next run records a new
     * version, and this write's audit row is the evidence between the two.
     */
    substitution_affected: z.boolean(),
    /** The `tg_party_alias_audit` row this write produced — the NFR-13 receipt. */
    audit: AuditReceiptSchema,
});
//# sourceMappingURL=manage-party-alias.js.map